import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Calendar,
  Clock,
  User,
  FileText,
  Phone,
  MapPin,
  Heart,
  CheckCircle,
  AlertCircle,
  Loader2,
  LogOut,
  Home
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientPortal() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [clientId, setClientId] = useState('');
  const [pin, setPin] = useState('');
  const [client, setClient] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [carePlan, setCarePlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [token, setToken] = useState('');

  useEffect(() => {
    // Check for existing session
    const savedToken = localStorage.getItem('clientPortalToken');
    const savedClient = localStorage.getItem('clientPortalClient');
    if (savedToken && savedClient) {
      setToken(savedToken);
      setClient(JSON.parse(savedClient));
      setIsAuthenticated(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && client) {
      fetchClientData();
    }
  }, [isAuthenticated, client]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!clientId.trim() || !pin.trim()) {
      toast.error('Please enter your Client ID and PIN');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/client-login`, null, {
        params: { client_id: clientId, pin: pin }
      });

      const { access_token, client: clientData } = response.data;
      setToken(access_token);
      setClient(clientData);
      setIsAuthenticated(true);
      
      localStorage.setItem('clientPortalToken', access_token);
      localStorage.setItem('clientPortalClient', JSON.stringify(clientData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      toast.success('Welcome to your care portal!');
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid Client ID or PIN');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setClient(null);
    setToken('');
    setSchedule([]);
    setCarePlan(null);
    localStorage.removeItem('clientPortalToken');
    localStorage.removeItem('clientPortalClient');
    delete axios.defaults.headers.common['Authorization'];
    toast.success('Logged out successfully');
  };

  const fetchClientData = async () => {
    try {
      const [scheduleRes, carePlansRes] = await Promise.all([
        axios.get(`${API_URL}/api/rota/client-portal/${client.id}`),
        axios.get(`${API_URL}/api/care-plans`, { params: { client_id: client.id } }),
      ]);

      setSchedule(scheduleRes.data);
      if (carePlansRes.data.length > 0) {
        setCarePlan(carePlansRes.data[0]);
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Client Care Portal</CardTitle>
            <CardDescription>Access your care plans and schedules</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Client ID</label>
                <Input
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  placeholder="Enter your Client ID"
                  data-testid="client-id-input"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">PIN</label>
                <Input
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  placeholder="Enter your PIN"
                  maxLength={6}
                  data-testid="client-pin-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-teal-600 hover:bg-teal-700"
                disabled={loading}
                data-testid="client-login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  'Access Portal'
                )}
              </Button>
            </form>
            <p className="text-xs text-center text-slate-500 mt-4">
              Contact your care provider if you don&apos;t have login details
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Portal Dashboard
  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-portal-dashboard">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-600 rounded-xl flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold">Care Portal</h1>
              <p className="text-sm text-slate-500">
                Welcome, {client?.first_name} {client?.last_name}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Quick Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Upcoming Visits</p>
                  <p className="text-2xl font-bold">{schedule.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Care Plan Status</p>
                  <p className="text-2xl font-bold capitalize">{carePlan?.status || 'Active'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Phone className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Emergency Contact</p>
                  <p className="text-lg font-semibold">{client?.emergency_contact_name || 'Not set'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 max-w-md">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="schedule">Schedule</TabsTrigger>
            <TabsTrigger value="care-plan">Care Plan</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Next Visit Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-teal-600" />
                    Next Visit
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {schedule.length > 0 ? (
                    <div className="space-y-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold">{schedule[0].scheduled_date}</p>
                          <p className="text-slate-500">{schedule[0].start_time} - {schedule[0].end_time}</p>
                        </div>
                        {schedule[0].status === 'en_route' ? (
                          <Badge className="bg-green-100 text-green-700">
                            <MapPin className="w-3 h-3 mr-1" />
                            En Route
                          </Badge>
                        ) : (
                          <Badge variant="outline">Scheduled</Badge>
                        )}
                      </div>
                      {schedule[0].care_worker_name && (
                        <div className="flex items-center gap-2 text-sm">
                          <User className="w-4 h-4 text-slate-400" />
                          <span>Care Worker: {schedule[0].care_worker_name}</span>
                        </div>
                      )}
                      {schedule[0].eta && (
                        <div className="p-3 bg-green-50 rounded-lg">
                          <p className="text-sm text-green-700 flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            {schedule[0].eta}
                          </p>
                        </div>
                      )}
                      <p className="text-sm text-slate-500 capitalize">
                        Visit Type: {schedule[0].visit_type?.replace('_', ' ')}
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <Calendar className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                      <p className="text-slate-500">No upcoming visits scheduled</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Personal Info Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5 text-teal-600" />
                    Your Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Name</p>
                      <p className="font-medium">{client?.first_name} {client?.last_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Date of Birth</p>
                      <p className="font-medium">{client?.date_of_birth}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Service Type</p>
                      <p className="font-medium capitalize">{client?.service_type?.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">NHS Number</p>
                      <p className="font-medium">{client?.nhs_number || 'Not recorded'}</p>
                    </div>
                  </div>
                  {client?.address && (
                    <div>
                      <p className="text-sm text-slate-500">Address</p>
                      <p className="font-medium flex items-center gap-2">
                        <Home className="w-4 h-4 text-slate-400" />
                        {client.address}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Upcoming Visits</CardTitle>
                <CardDescription>Your scheduled care visits</CardDescription>
              </CardHeader>
              <CardContent>
                {schedule.length > 0 ? (
                  <div className="space-y-4">
                    {schedule.map((visit, index) => (
                      <div
                        key={visit.id || index}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center">
                            <Calendar className="w-6 h-6 text-teal-600" />
                          </div>
                          <div>
                            <p className="font-semibold">{visit.scheduled_date}</p>
                            <p className="text-sm text-slate-500">
                              {visit.start_time} - {visit.end_time}
                            </p>
                            {visit.care_worker_name && (
                              <p className="text-sm text-slate-500 mt-1">
                                <User className="w-3 h-3 inline mr-1" />
                                {visit.care_worker_name}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          {visit.status === 'en_route' ? (
                            <Badge className="bg-green-100 text-green-700">
                              <MapPin className="w-3 h-3 mr-1" />
                              En Route
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="capitalize">
                              {visit.status || 'Scheduled'}
                            </Badge>
                          )}
                          <p className="text-xs text-slate-500 mt-1 capitalize">
                            {visit.visit_type?.replace('_', ' ')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No upcoming visits scheduled</p>
                    <p className="text-sm text-slate-400 mt-1">
                      Contact your care provider for more information
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Care Plan Tab */}
          <TabsContent value="care-plan" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-teal-600" />
                  Your Care Plan
                </CardTitle>
                <CardDescription>
                  Your personalized care plan and objectives
                </CardDescription>
              </CardHeader>
              <CardContent>
                {carePlan ? (
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-semibold mb-2">{carePlan.title}</h3>
                      <Badge variant="outline" className="capitalize">
                        Status: {carePlan.status}
                      </Badge>
                    </div>

                    {carePlan.objectives?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-sm text-slate-500 mb-3">Care Objectives</h4>
                        <ul className="space-y-2">
                          {carePlan.objectives.map((objective, i) => (
                            <li key={i} className="flex items-start gap-2 p-3 bg-teal-50 rounded-lg">
                              <CheckCircle className="w-5 h-5 text-teal-600 mt-0.5" />
                              <span className="text-sm">{objective}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {carePlan.interventions?.length > 0 && (
                      <div>
                        <h4 className="font-medium text-sm text-slate-500 mb-3">Care Interventions</h4>
                        <ul className="space-y-2">
                          {carePlan.interventions.map((intervention, i) => (
                            <li key={i} className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
                              <Heart className="w-5 h-5 text-blue-600 mt-0.5" />
                              <span className="text-sm">{intervention}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="pt-4 border-t">
                      <p className="text-sm text-slate-500">
                        Review Date: {carePlan.review_date}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No care plan available</p>
                    <p className="text-sm text-slate-400 mt-1">
                      Contact your care provider for more information
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
